import React, { Component } from 'react'
import { connect } from 'react-redux'
import classnames from 'classnames'
import { getViewportHeight } from '../app/selectors'
import { getCaseStudyColours, getCaseStudyFetching } from '../case-studies-container/selectors'

import {
  navOverArtists,
  navOutArtists
} from '../navigation/actions'
import {
  setMainFooterActive
} from '../main/actions'
import {
  currentPageName
} from '../app/actions'
import {
  getLocation,
  getPreviousLocation
} from '../app/selectors'
import './layer-page.css'
import { PAGE_TRANSITION_MS } from '../../constants'
import {
  navigateUp
} from '../../lib/historyUtil'

import {
  createEnterPage,
  createEnterCompletePage,
  createExitPage,
  createExitCompletePage,
  createSetPageScroll,
  createSetPageScrollEnd,
  createUpdateScroll,
  createUpdateHeight,
  createSaveScroll,
  createUnsetReset
} from './create-page-actions'

const createPage = (PageComponent, pageName, autoNavigate, immediateTransition) => {

  const enterPage = createEnterPage(pageName)
  const enterCompletePage = createEnterCompletePage(pageName)
  const exitPage = createExitPage(pageName)
  const exitCompletePage = createExitCompletePage(pageName)
  const setScroll = createSetPageScroll(pageName)
  const saveScroll = createSaveScroll(pageName)
  const updateScroll = createUpdateScroll(pageName)
  const updateHeight = createUpdateHeight(pageName)
  const setScrollEnd = createSetPageScrollEnd(pageName)
  const unsetReset = createUnsetReset(pageName) 

  class LayerPage extends Component {
    constructor(props) {
      super(props)

      this.state = {
        spacerOpacity: 1,
        internalActive: false,
        transitionFade: false
      }

      this.throttledScroll = this.throttle(this.handleScroll, 10, this)
    }
    
    componentDidMount() {
      const { match } = this.props 

      if (match && match.isExact) {
        this.beginEnterPage(false)
      }
    }
 
    componentDidUpdate(prevProps) {
      const { 
        match: prevMatch } = prevProps 
      const { 
        match: nextMatch } = this.props

      if (prevMatch !== nextMatch) {

        // Matches do not match, so there has been a route change
        // Match could be null or and object with `isExact` prop
        // to tell us if we are on the exact route 
        // const prevExactMatch = !!(prevMatch && prevMatch.isExact)
        const nextExactMatch = !!(nextMatch && nextMatch.isExact)

        // eg. Navigation to home page
        // We are on collection page and go back to homepage
        // prevMatch will not be null (as homepage matches with collection route)
        // nextMatch will not be null and will `isExact`

        // If prevMatch is null, we need to transition in this page
        // Otherwise we dont. 

        if (nextExactMatch) {
          // This page matches the exact route 
          // Determine if need to transition (ie. already visible in page or not)

          if (prevMatch === null || typeof prevMatch === 'undefined') {
            // We have no match for this page before, so it must have been transitioned
            // out of the screen, or never existed on page, so bring back in 
            this.beginEnterPage(true)
          } else {
            // We do have a match for this page previously, so it must be 
            // visible on screen, no need for transition 
            this.beginEnterPage(false)
          }
        } else {
          // This page does not match the current route 
          // If next match is null, the page needs to exit transition
          // If next match is true, we can leave it here, but set 
          // active to false
          
          if (nextMatch === null || typeof nextMatch === 'undefined') {
            // Begin transitioning 

            // Special case for artist page if permalinked subcollection 
            // navigating back to homepage 
            if (pageName === 'ARTIST' && (this.props.previousLocation === null && this.props.currentLocation === null)) {
              this.destroyPage()
            } else {
              this.beginExitPage(true)
            }
          
          } else {
            // Set to inactive 
            this.destroyPage()
          }  
        }
      } 

      this.getInnerRect()
    }

    componentWillReceiveProps(nextProps) {
      const {
        reset: nextReset,
        targetSt: nextTargetSt
      } = nextProps
      const {
        reset: prevReset,
        targetSt: prevTargetSt
      } = this.props

      if (nextReset !== prevReset) {
        if (nextReset) {
          this.setElementScroll(0)

          const { onReset } = this.props 
          onReset && onReset()
        }
      }

      if (nextTargetSt !== prevTargetSt) {
        this.setElementScroll(nextProps.targetSt)
      }
    }

    beginEnterPage(withTransition) {
      const { 
        doEnterPage,
        doEnterCompletePage
      } = this.props 

      // Whether we are transitioning or not 
      // We will fire enter page action 
      doEnterPage()

      if (withTransition) {

        if (pageName !== 'HOME') {
          this.props.onUpdateScroll(0)
          this.setElementScroll(0)
        }
        
        // If we have a transition flag
        //  set a timeout to fire complete after transition completes
        const self = this
        setTimeout(() => {
          doEnterCompletePage()
          self.initPage()
        }, PAGE_TRANSITION_MS)
      } else {
        // If not transitioning, we can fire complete instantly
        doEnterCompletePage()
        this.initPage()
      }
    }

    // Handle the currently active page becoming active
    // If the url changes and this is no longer the active page
    // Homepage - Remain where we are 
    // Any page which is navigated UP (../) must be removed
    // Any page which is still within the url, can stay 
    beginExitPage(withTransition) {
      const { 
        doExitPage,
        doExitCompletePage
      } = this.props 

      doExitPage()
      this.destroyPage()

      if (withTransition) {
        setTimeout(() => {
          doExitCompletePage()
        }, PAGE_TRANSITION_MS)
      } else {
        doExitCompletePage()
      }
    }

    resetState() {
      this.setState({
        internalActive: false,
        transitionFade: false
      })
    }

    destroyPage() {
      this.resetState()
      this.clearLoadTimeout()
      this.clearScrollEndTimer()
      this.removeListeners()
      this.clearSmoothScrollTimer()
      this.init = false
    }

    initPage() {
      const { match, setCurrentPage } = this.props
      
      if (match && match.isExact) {
        setCurrentPage && setCurrentPage(pageName)

        if (!this.init) {
          this.getInnerRect()
          this.addListeners()
          this.setInternalActive(true)
          this.init = true
        }
      }
    }

    addListeners() {
      if (this.el) this.el.addEventListener('scroll', this.throttledScroll)
      window.addEventListener('loadmedia', this.handleLoadMedia)
    }

    removeListeners() {
      if (this.el) this.el.removeEventListener('scroll', this.throttledScroll)
      window.removeEventListener('loadmedia', this.handleLoadMedia)
    }

    startLoadTimeout() {
      this.loadTimeout = setTimeout(this.handleLoadTimeout, 80)
    }

    clearLoadTimeout() {
     if (this.loadTimeout) {
       clearTimeout(this.loadTimeout)
       this.loadTimeout = null
     }
    }

    handleLoadTimeout = () => {
      this.getInnerRect()
    }

    handleLoadMedia = () => {
      this.clearLoadTimeout()
      this.startLoadTimeout()
    }

    startScrollEndTimer = () => {
      const { onStartScrollEnd } = this.props
      onStartScrollEnd && onStartScrollEnd()

      this.scrollEndTimeout = setTimeout(this.handleScrollEndTimeout, 0)
    }

    clearScrollEndTimer = () => {
      if (this.scrollEndTimeout) {
        clearTimeout(this.scrollEndTimeout)
        this.scrollEndTimeout = null
      }
    }

    handleScrollEndTimeout = () => {
      const { onScrollEnd } = this.props
      onScrollEnd && onScrollEnd()
    }

    throttle(fn, threshold, context) {
      let last
      let deferTimer

      return function () {
        let ctx = context || this 
        let now = +new Date()
        let args = arguments

        if (last && now < last + threshold) {
          clearTimeout(deferTimer)
          deferTimer = setTimeout(() => {
            last = now
            fn.apply(ctx, args)
          }, threshold)
        } else {
          last = now
          fn.apply(ctx, args)
        }
      }
    }

    setInternalActive(active) {
      this.setState({
        internalActive: active
      })
    }

    setElementScroll = (st = 0) => {
      if (this.el) {
        this.el.scrollTop = st
      }
    }

    clearSmoothScrollTimer() {
      if (this.smoothScrollTimeout) {
        clearTimeout(this.smoothScrollTimeout)
        this.smoothScrollTimeout = null
      }
    }

    startSmoothScrollTimer() {
      this.smoothScrollTimeout = setTimeout(this.handleSmoothScroll, this.intervalMs)
    }

    handleSmoothScroll = () => {
      if (this.el) {
        this.prevSt += this.stepSt
        this.el.scrollTop = this.prevSt
      }

      if (++this.intervalCount < this.numSteps) {
        this.startSmoothScrollTimer()
      }
    }

    setSmoothScroll = (target = 0) => {
      this.totalDurationMs = 200
      this.intervalMs = 5
      this.intervalCount = 0
      this.prevSt = this.el.scrollTop
      this.numSteps = parseInt(this.totalDurationMs / this.intervalMs, 10)
      this.stepMs = this.intervalMs / this.totalDurationMs
      this.stepSt = (target - this.prevSt) * this.stepMs

      this.clearSmoothScrollTimer()
      this.startSmoothScrollTimer()
    }
    
    handleScroll = e => {
      const st = e.target.scrollTop
      const pageHeight = this.pageHeight
      const { onUpdateScroll, vh, isScrollEnd } = this.props
      const atBottom = st >= pageHeight - vh

      if ((st + vh) > (pageHeight - vh)) {
        // Can see spacer, but how much?
        let spacerDelta = (st + vh) - (pageHeight - vh)        
        spacerDelta /= vh

        const spacerOpacity = Math.max(0, 1 - spacerDelta)

        this.setState({
          spacerOpacity: spacerOpacity
        })
      }

      // If scrolled, make sure scroll end timeout is cancelled
      this.clearScrollEndTimer()
      
      if (atBottom) {
        if (autoNavigate) {
          this.startAutoNavigate()
        } else {
          if (!isScrollEnd) {
            this.clearScrollEndTimer()
            this.startScrollEndTimer()
          }
        }
      }

      onUpdateScroll && onUpdateScroll(st)
    }

    getInnerRect = () => {
      const { onUpdateHeight } = this.props 

      this.pageHeight = parseInt(this.innerEl ? this.innerEl.getBoundingClientRect().height : 0, 10)

      onUpdateHeight && onUpdateHeight(this.pageHeight)
    }

    // Fired on scroll to bottom to navigate back
    // Apply transition state so on route
    // change, transition will fade instead of exit up
    startAutoNavigate() {
      if (!this.navigating) {
        const { match, history, previousLocation } = this.props 
        navigateUp(match, history, previousLocation)

        this.navigating = true
        setTimeout(() => {
          this.navigating = false
        }, 100)
      } 
    }

    render() {
      const { 
        isScrollEnd,
        pageExit,
        pageExitComplete,
        pageEnter,
        pageEnterComplete } = this.props 

      const { 
        spacerOpacity,
        internalActive,
        transitionFade } = this.state

      const layerPageNameClass = `layer-page--${pageName.toLowerCase()}`

      const layerPageClassNames = classnames({
        [layerPageNameClass]: true,
        'layer-page--active': internalActive,
        'layer-page--enter': pageEnter,
        'layer-page--enter-complete': pageEnterComplete,
        'layer-page--exit': pageExit,
        'layer-page--exit-complete': pageExitComplete,
        'layer-page--is-scroll-next-to-end': spacerOpacity < 1,
        'layer-page--is-scroll-end': isScrollEnd,
        'layer-page--transition-fade': transitionFade
      }, 'layer-page')
      
      return (
        <div ref={el => this.el = el} className={layerPageClassNames}>
          <div ref={innerEl => this.innerEl = innerEl} className="layer-page__content">
            <PageComponent 
              setSmoothScroll={this.setSmoothScroll}
              pageName={pageName} 
              setElementScroll={this.setElementScroll}
              {...this.props} />
            <div style={{ opacity: spacerOpacity }} className="layer-page__spacer"></div>
          </div>
          {/*
          <div 
            style={{ top: `${this.state.oldSt}px` }} 
            className="layer__overlay"></div>*/}

        </div>
      )
    }
  }

  
  const mapStateToProps = state => {
    const pageNameRef = `page_${pageName.toLowerCase()}`

    return {
      currentLocation: getLocation(state),
      previousLocation: getPreviousLocation(state),
      st: state[pageNameRef].get('st'),
      pageEnter: state[pageNameRef].get('pageEnter'),
      pageEnterComplete: state[pageNameRef].get('pageEnterComplete'),
      pageExit: state[pageNameRef].get('pageExit'),
      pageExitComplete: state[pageNameRef].get('pageExitComplete'),
      vh: getViewportHeight(state),
      isScrollEnd: state[pageNameRef].get('isScrollEnd'),
      reset: state[pageNameRef].get('reset'),
      targetSt: state[pageNameRef].get('targetSt'),
      colours: getCaseStudyColours(state),
      fetching: getCaseStudyFetching(state),
    }
  }

  const mapDispatchToProps = dispatch => {
    return {
      setCurrentPage: pageName => {
        dispatch(currentPageName(pageName))
      },
      doEnterPage: () => {
        dispatch(enterPage())
      },
      doEnterCompletePage: () => {
        dispatch(enterCompletePage())
      },
      doExitPage: () => {
        dispatch(exitPage())
      },
      doExitCompletePage: () => {
        dispatch(exitCompletePage())
      },
      onSaveScroll: st => {
        dispatch(saveScroll(st))
      },
      onSetScroll: st => {
        dispatch(setScroll(st))
      },
      onUpdateScroll: st => {
        dispatch(updateScroll(st))
      },
      onUpdateHeight: ph => {
        dispatch(updateHeight(ph))
      },
      onStartScrollEnd: () => {
        dispatch(navOverArtists())
        dispatch(setMainFooterActive(true))
      },
      onScrollEnd: () => {
        // dispatch(navOverArtists())
        dispatch(setScrollEnd(true))
        // dispatch(setMainFooterActive(true))
      },
      onUnComplete: () => {
        dispatch(navOutArtists())
        dispatch(setScrollEnd(false))
        dispatch(setMainFooterActive(false))
      },
      onReset: () => {
        dispatch(unsetReset())
      }
    }
  }

  return connect(mapStateToProps, mapDispatchToProps)(LayerPage)
}

export default createPage



// WEBPACK FOOTER //
// ./src/components/create-page/index.js